import { Wrapper } from '@googlemaps/react-wrapper';
import type { GetServerSidePropsContext, NextPage } from 'next';
import Image from 'next/image';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import Container from '../../components/container/container.component';
import Maps from '../../components/maps/maps.component';
import { getPage } from '../../lib';
import styles from '../../styles/[page].module.scss';
import PageTemplate from '../../templates/page.template';
import { PageProps } from '../../types/page-props.interface';

interface Props extends PageProps { }

const VirtualSightseeing: NextPage<Props> = ({ menu, options, page }) => (
  <PageTemplate
    menu={menu}
    options={options}
    title={page.title}
    description={page.description}
  >
    <Container
      css={{ height: 'auto', minHeight: 'calc(100vh - 240px)' }}
      column={true}
    >
      <article className={styles.content}>
        <h1>{page.title}</h1>
        <p>
          Currently, Berenike is located in the restricted area, which is why it
          cannot be visited by people not involved in the archaeological
          excavations. The aim of this webpage is to overcome this barrier and
          provide the opportunity to visit Berenike to all those who have so far
          been unable to do so. This goal is achieved by creating a virtual tour
          around Berenike using the publicly available Google Street View
          environment integrated with Google Maps. We provide you with spherical
          images and photo tours.
        </p>
        <p>How to go to Street View:</p>
        <p style={{ textIndent: 0, marginLeft: '40px' }}>
          1. The little yellow human figure in the bottom right corner of the
          map below has to be dropped on a selected blue circle. <br />
          2. This opens a 360° panorama of the selected part of the site.
          <br />
          3. Tap on a picture and move the cursor to turn around in the chosen
          direction.
        </p>
        <div style={{ width: '25%', marginTop: '20px', marginBottom: '20px' }}>
          <Zoom>
            <Image
              src="http://localhost:3000/Infografika 1.jpg"
              alt="Illustration"
              layout="responsive"
              width={4422}
              height={1893}
            />
          </Zoom>
        </div>
        <p>
          You can also visit Berenike using Google Maps (
          <a
            href="https://www.google.pl/maps/@23.9120624,35.4734346,16z?entry=ttu"
            target="_blank"
            rel="noreferrer"
            className="externalLink"
          >
            https://www.google.pl/maps/@23.9120624,35.4734346,16z?entry=ttu
          </a>
          ):
        </p>
        <p style={{ textIndent: 0, marginLeft: '40px' }}>
          1. Choose the satellite map mode in the bottom left corner of the
          webpage. <br />
          2. Then tap “Street View” at the top to show the blue lines and blue
          circles showing the extent of Street View coverage. <br />
          3. Tap on any blue line/circle to enter the Street View mode. <br />
        </p>
        <div style={{ width: '25%', marginTop: '20px', marginBottom: '20px' }}>
          <Zoom>
            <Image
              src="http://localhost:3000/Infografika 2.jpg"
              alt="Illustration"
              layout="responsive"
              width={5478}
              height={4326}
            />
          </Zoom>
        </div>
        <Wrapper apiKey={'AIzaSyCR4lPUGkFVuPYTe6n7Do6L01txiEzdEuA'}>
          <Maps />
        </Wrapper>
        <div style={{ width: '100%', marginTop: '20px' }}>
          <Image
            src="http://localhost:3000/2023 Mapa całość_3_Mapa z Legendą.jpg"
            alt="Illustration"
            layout="responsive"
            width={2474}
            height={2257}
          />
        </div>
        <p>
          If you&apos;d like us to expand the virtual tour of Berenike, please
          inform us via email (
          <a href="mailto:berenike@uw.edu.pl" className="externalLink">
            berenike@uw.edu.pl
          </a>
          ). Just let us know what you&apos;d like to see, and we&apos;ll do our
          best to capture a series of photos during the upcoming excavation
          season.
        </p>
        <p>
          To view three-dimensional models of selected parts of the site, go to{' '}
          <a
            href="https://virtualberenike.pcma.uw.edu.pl/"
            target="_blank"
            rel="noreferrer"
            className="externalLink"
          >
            Virtual Berenike
          </a>
          .
        </p>
      </article>
    </Container>
  </PageTemplate>
);

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const page = await getPage('virtual-sightseeing');

  if (!page.page) {
    return {
      notFound: true,
    };
  }

  ctx.res.setHeader('Cache-Control', 'public, max-age=300, s-maxage=300');

  return {
    props: page,
  };
}

export default VirtualSightseeing;
